import React, {useEffect, useRef, useState} from 'react';
import Navbar from '../components/NavBar';
import Footer from '../components/Footer';
import DashboardCard from '../components/DashboardCard';
import {useNavigate} from 'react-router-dom';
import Client from "../models/Client";
import FollowUp from "../models/FollowUp";
import AddClientModal from "../components/AddClientModal";
import ErrorToast from "../components/ErrorToast";
import {getDashboardTotals, getRecentClients} from "../services/client-service";
import {useAuth} from "../context/AuthContext";
import {getRecentFollowUps} from "../services/follow-up-service";
import DashboardTotals from "../models/DashboardTotals";
import {formatFollowUpTime} from "../utils/DateTimeUtils";
import {ClientStatus} from "../models/enums/ClientStatus";
import UpgradeButton from "../components/UpgradeButton";
import {SubscriptionType} from "../models/enums/SubscriptionType";

const DashboardPage: React.FC = () => {
    const navigate = useNavigate();
    const [isAddClientModalOpen, setIsAddClientModalOpen] = useState(false);
    const [errorToast, setErrorToast] = useState('');
    const [clients, setClients] = useState([] as Client[]);
    const [followUps, setFollowUps] = useState([] as FollowUp[]);
    const [dashboardTotals, setDashboardTotals] = useState({clients: 0, leads: 0, followUps: 0} as DashboardTotals)
    const {user, subscriptionPlan} = useAuth();
    const isFirstDashboardTotalRender = useRef(true);
    const [disableAddClient, setDisableAddClient] = useState(true);
    const [loadingClients, setLoadingClients] = useState(true);
    const [loadingFollowUps, setLoadingFollowUps] = useState(true);

    useEffect(() => {
      const fetchDashboardTotals = async () => {
          try {
              if (user?.id) {
                  const response = await getDashboardTotals(user.id);
                  // @ts-ignore
                  setDashboardTotals(response.data)
              }
          } catch (error) {
              console.error("Unable to retrieve dashboard totals", error)
          }

      };
      if (isFirstDashboardTotalRender.current) {
          isFirstDashboardTotalRender.current = false;
      } else {
          fetchDashboardTotals();
      }
    }, [clients]);

    useEffect(() => {
        const fetchRecentClients = async () => {
            setLoadingClients(true);
            try {
                if (user?.id) {
                    const response = await getRecentClients(user.id);
                    if (response.status === 200 && response.data) {
                        setClients(response.data);
                    }
                }
            } catch (error) {
                console.error("Error fetching recent clients:", error);
                setErrorToast("Error retrieving client list");
            }
            finally {
                setLoadingClients(false);
            }
        };

        fetchRecentClients();
    }, [user]);

    useEffect(() => {
        const fetchRecentFollowUps = async () => {
            setLoadingFollowUps(true);
            try {
                if (user?.id) {
                    const response = await getRecentFollowUps(user.id);
                    if (response.status === 200 && response.data) {
                        setFollowUps(response.data);
                    }
                }
            } catch (error) {
                console.error('Error fetching recent follow-ups', error);
                setErrorToast("Error retrieving follow up list");
            } finally {
                setLoadingFollowUps(false);
            }
        }

        fetchRecentFollowUps();
    }, [user?.id]);

    useEffect(() => {
        if (subscriptionPlan?.maxClients && dashboardTotals?.clients >= subscriptionPlan.maxClients) {
            setDisableAddClient(true);
        } else {
            setDisableAddClient(false);
        }
    }, [subscriptionPlan, dashboardTotals.clients]);

    const handleAddClient = (newClient: Client) => {
        setClients(prev => {
            const updated = [newClient, ...prev];
            updated.sort((a, b) => b.lastActivityDate.getTime() - a.lastActivityDate.getTime());
            return updated.slice(0, 5);
        });
    };

    return (
        <div className="min-h-screen flex flex-col">
            <Navbar />

            <main className="flex-grow container mx-auto px-4 py-6">
                {subscriptionPlan?.subscriptionType === SubscriptionType.FREE && (
                    <div className="flex justify-end mb-4" data-testid='top-upgrade'>
                        <UpgradeButton />
                    </div>
                )}
                {/* Header Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                    <DashboardCard label="Total Clients" value={dashboardTotals.clients} route="/clients" />
                    <DashboardCard label="Follow Ups Due" value={dashboardTotals.followUps} route="/follow-ups" />
                    <DashboardCard
                        label="New Leads"
                        value={dashboardTotals.leads}
                        route="/clients?filter=lead"
                        navigateState={{ filters: { status: ClientStatus.LEAD } }}
                    />
                </div>

                {/* Upcoming Follow Ups */}
                <div className="mb-8" data-testid='follow-ups'>
                    <h2 className="text-xl font-semibold mb-2">Upcoming Follow Ups</h2>

                    {loadingFollowUps ? (
                        <div
                            className="border rounded p-4 text-center text-gray-500 animate-pulse"
                            data-testid="follow-ups-loading"
                        >
                            <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto mb-2"></div>
                            <div className="h-4 bg-gray-200 rounded w-1/3 mx-auto"></div>
                        </div>
                    ) : followUps.length > 0 ? (
                        <ul className="divide-y border rounded" data-testid='follow-up-list'>
                            {followUps.map(followUp => {
                                const now = new Date();
                                const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                                const startOfTomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);

                                const isOverdue  = followUp.followUpDate < startOfToday;
                                const isDueToday = followUp.followUpDate >= startOfToday && followUp.followUpDate < startOfTomorrow;
                                const timeDisplay = formatFollowUpTime(followUp);

                                return (
                                    <li
                                        key={followUp.id}
                                        data-testid={`follow-up-${followUp.id}`}
                                        className={`p-4 hover:bg-gray-50 cursor-pointer ${
                                            isOverdue ? 'bg-red-50'
                                                : isDueToday ? 'bg-yellow-50' 
                                                : ''
                                        }`}
                                        onClick={() => navigate(`/client/${followUp.clientId}`)}
                                    >
                                        <div className="flex justify-between items-start">
                                            <span className="font-medium">{followUp.name}</span>
                                            <div className="text-right">
                                                <div className="text-sm text-gray-500">
                                                    {followUp.followUpDate.toLocaleDateString('en-US')}
                                                    {isOverdue && (
                                                        <span data-testid="overdue" className="ml-2" title="Overdue">⚠️</span>
                                                    )}
                                                    {isDueToday && (
                                                        <span data-testid="due-today" className="ml-2" title="Due today">📅</span>
                                                    )}
                                                </div>
                                                <div
                                                    className="mt-1 text-xs text-gray-600"
                                                    data-testid={`follow-up-time-${followUp.id}`}
                                                >
                                                    {timeDisplay ?? '—'}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="text-sm text-gray-500">Type: {followUp.contactOption}</div>
                                    </li>
                                );
                            })}
                        </ul>
                    ) : (
                        <div
                            className="text-center text-gray-500 py-6 border rounded"
                            data-testid="no-follow-ups"
                        >
                            No follow-ups scheduled. Try setting some reminders!
                        </div>
                    )}
                </div>

                {/* Recent Clients */}
                <div className="mb-8" data-testid='recent-clients'>
                    <div className="flex justify-between items-center mb-2">
                        <h2 className="text-xl font-semibold">Recent Client Activity</h2>
                        <div className="flex items-center gap-3" data-testid='client-actions'>
                            {disableAddClient && subscriptionPlan?.subscriptionType === SubscriptionType.FREE && (
                                <UpgradeButton />
                            )}
                            <button
                                className={`px-4 py-2 rounded ${
                                    disableAddClient
                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed hover:bg-gray-300'
                                        : 'bg-crumborange text-white hover:bg-orange-600'
                                }`}
                                onClick={() => setIsAddClientModalOpen(true)}
                                disabled={disableAddClient}
                                data-testid="add-client-btn"
                                aria-disabled={disableAddClient}
                                title={disableAddClient ? 'Reached max clients for your plan' : undefined}
                            >
                                Add Client
                            </button>
                        </div>
                    </div>

                    {disableAddClient && subscriptionPlan?.maxClients != null && (
                        <div className="mb-3 text-sm text-gray-600" data-testid="client-limit-message">
                            You’ve reached the maximum of <span className="font-semibold">{subscriptionPlan.maxClients}</span> clients for your plan.
                            <span className="ml-1">Upgrade your plan to add more.</span>
                        </div>
                    )}

                    {loadingClients ? (
                        <div
                            className="border rounded p-4 text-center text-gray-500 animate-pulse"
                            data-testid="clients-loading"
                        >
                            <div className="h-4 bg-gray-200 rounded w-2/3 mx-auto mb-2"></div>
                            <div className="h-4 bg-gray-200 rounded w-1/3 mx-auto"></div>
                        </div>
                    ) : clients.length > 0 ? (
                        <ul className="divide-y border rounded" data-testid='client-list'>
                            {clients.map(client => (
                                <li
                                    key={client.id}
                                    data-testid={`client-${client.id}`}
                                    className="p-4 hover:bg-gray-50 cursor-pointer"
                                    onClick={() => navigate(`/client/${client.id}`)}
                                >
                                    <div className="flex justify-between items-center">
                                        <span className="font-medium">{client.name}</span>
                                        <span className="text-sm bg-gray-200 rounded-full px-2 py-1">
                                            {client.status}
                                        </span>
                                    </div>
                                    <div className="text-sm text-gray-600 mt-1 flex flex-wrap items-center gap-2">
                                        <span className="text-gray-700">Last activity:</span>
                                        {client.lastActivity ? (
                                            <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-800 px-2 py-0.5 rounded-full">
                                                <svg
                                                    className="w-4 h-4 text-crumbblue"
                                                    viewBox="0 0 20 20"
                                                    fill="currentColor"
                                                    aria-hidden="true"
                                                >
                                                    <path
                                                        fillRule="evenodd"
                                                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm.75-12a.75.75 0 00-1.5 0v4.25c0 .199.079.39.22.53l2.5 2.5a.75.75 0 101.06-1.06l-2.28-2.28V6z"
                                                        clipRule="evenodd"
                                                    />
                                                </svg>
                                                <span>{client.lastActivity}</span>
                                            </span>
                                        ) : (
                                            <span className="text-gray-400 italic">No recent activity</span>
                                        )}
                                        <span className="hidden sm:inline text-gray-400">•</span>
                                        <span className="text-gray-500">
                                            {client.lastActivityDate?.toLocaleDateString('en-US')}
                                        </span>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <div
                            className="text-center text-gray-500 py-6 border rounded"
                            data-testid="no-clients"
                        >
                            You haven't added any clients yet. Click "Add Client" to get started!
                        </div>
                    )}
                </div>

            </main>
            <Footer />
            <ErrorToast data-testid="error-toast" message={errorToast} clearError={() => setErrorToast('')} />
            <AddClientModal
                onAddClient={handleAddClient}
                isOpen={isAddClientModalOpen}
                onClose={() => setIsAddClientModalOpen(false)}
                onError={(error) => setErrorToast(error)}/>
        </div>
    );
};

export default DashboardPage;